import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PaperPlaneTilt, Sparkle, Trash, Crown } from "@phosphor-icons/react";

const SESSION_KEY = "aigov_chat_session";

const SUGGESTIONS = [
  "Walk me through your third-party AI assessment process.",
  "Difference between EU AI Act Provider vs Deployer?",
  "What is TEVV in NIST AI RMF?",
  "How do I test for prompt injection in a vendor LLM?",
];

export default function Tutor() {
  const { user } = useAuth();
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = crypto.randomUUID(); localStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("aigov_token");
    fetch(`${API}/chat/history/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((data) => setMessages(data.map((m) => ({ role: m.role, content: m.content }))));
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("aigov_token");
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const chunk = p.slice(5).trimStart();
          if (chunk === "[DONE]") continue;
          if (chunk.startsWith("[error]")) continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — the tutor is unavailable right now." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  if (!user?.is_pro) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 space-y-6 fade-up" data-testid="tutor-paywall">
        <div className="w-16 h-16 bg-primary text-primary-foreground flex items-center justify-center mx-auto">
          <Crown size={28} weight="fill" />
        </div>
        <div className="overline">Pro feature</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter leading-[0.95]">The AI Tutor is a Pro feature.</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Upgrade to unlock unlimited Claude-Sonnet-powered coaching on NIST AI RMF, ISO 42001, EU AI Act, and third-party assessment scenarios.
        </p>
        <Link to="/app/pricing" data-testid="tutor-upgrade-cta"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm font-medium hover:bg-[color:hsl(223_100%_28%)] transition-colors">
          <Crown size={16} weight="fill" /> See Pro plan
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] md:h-[calc(100vh-4rem)] -m-6 lg:-m-10">
      <header className="border-b border-border bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary text-primary-foreground flex items-center justify-center"><Sparkle size={16} weight="fill" /></div>
          <div>
            <div className="font-display text-xl leading-none">AI Tutor</div>
            <div className="text-xs text-muted-foreground mt-1">Powered by Claude Sonnet · Ask anything AI Governance</div>
          </div>
        </div>
        <button onClick={clearSession} className="text-xs flex items-center gap-1.5 text-muted-foreground hover:text-accent" data-testid="tutor-clear">
          <Trash size={14} /> New session
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8 bg-background" data-testid="tutor-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto text-center">
            <div className="overline mb-3">Try one of these</div>
            <h2 className="font-display text-3xl tracking-tighter mb-8">What would you like to master today?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} className="text-left border border-border p-4 hover:border-foreground bg-white transition-colors text-sm"
                  data-testid={`tutor-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed p-4 border ${
                m.role === "user"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-white text-foreground border-border"
              }`} data-testid={`tutor-msg-${m.role}-${i}`}>
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border bg-white px-6 py-4">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            disabled={streaming}
            placeholder="Ask about NIST AI RMF, ISO 42001, EU AI Act, red-teaming, vendor DD…"
            className="flex-1 border border-border px-4 py-3 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm"
            data-testid="tutor-input" />
          <button type="submit" disabled={streaming || !input.trim()}
            className="px-4 py-3 bg-primary text-primary-foreground text-sm flex items-center gap-2 disabled:opacity-50 hover:bg-[color:hsl(223_100%_28%)] transition-colors"
            data-testid="tutor-send">
            <PaperPlaneTilt size={16} weight="fill" /> Send
          </button>
        </form>
      </div>
    </div>
  );
}
